import { useCallback, useEffect, useRef, useState } from "react";
import {
  Send, Paperclip, X, ArrowLeft, Search, Inbox as InboxIcon, SendHorizonal,
  UserRound, Stethoscope, FileText, ShieldCheck, Ticket, MessageSquare, Headset,
} from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { useAuth } from "@/context/AuthContext";
import { useMessaging } from "@/context/MessagingContext";

export const TICKET_STATUS_META = {
  open: { label: "Open", cls: "open" },
  in_progress: { label: "In progress", cls: "inprog" },
  resolved: { label: "Resolved", cls: "resolved" },
};

export function StatusBadge({ status }) {
  const m = TICKET_STATUS_META[status] || TICKET_STATUS_META.open;
  return <span className={`tkt-status ${m.cls}`} data-testid={`ticket-status-${status || "open"}`}>{m.label}</span>;
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}
function fmtDay(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return ""; }
}

/* Gmail-like thread panel for one ticket (admin replies inline) */
export function TicketThread({ ticket, kind, onBack, onSent, onStatusChange, viewer = "admin" }) {
  const { user } = useAuth();
  const { tick, refreshSummary } = useMessaging();
  const [msgs, setMsgs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState(ticket.status || "open");
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const logRef = useRef(null);
  const scrollDown = () => setTimeout(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, 60);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/conversations/${ticket.id}/messages`);
      setMeta({ name: data.counterpart_name, role: data.counterpart_role, ticket_code: data.conversation?.ticket_code, subject: data.conversation?.subject });
      if (data.conversation?.status) setStatus(data.conversation.status);
      setMsgs(data.messages || []);
      scrollDown();
      refreshSummary();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  }, [ticket.id, refreshSummary]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { load(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeStatus = async (next) => {
    const prev = status;
    setStatus(next);
    try {
      await api.patch(`/admin/tickets/${ticket.id}/status`, { status: next });
      toast(`Ticket marked ${TICKET_STATUS_META[next].label}`);
      onStatusChange && onStatusChange();
    } catch (e) { setStatus(prev); toast(formatApiError(e.response?.data?.detail)); }
  };

  const addFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length) setFiles((p) => [...p, ...list]);
    e.target.value = "";
  };
  const send = async () => {
    const t = text.trim();
    if (!t && files.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", t);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/conversations/${ticket.id}/messages`, fd);
      setMsgs((prev) => [...prev, data.message]);
      setText(""); setFiles([]); scrollDown();
      onSent && onSent();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const KindIcon = kind === "patient" ? UserRound : kind === "doctor" ? Stethoscope : Headset;

  return (
    <div className="tkt-thread" data-testid="ticket-thread">
      <div className="tkt-thread-head">
        <button className="ib-iconbtn" onClick={onBack} data-testid="ticket-thread-back"><ArrowLeft size={16} /></button>
        <span className={`ib-avatar ${kind}`}><KindIcon size={16} /></span>
        <div className="tkt-thread-meta">
          <b data-testid="ticket-thread-name">{ticket.name}</b>
          <span className="tkt-thread-sub">
            {(meta?.ticket_code || ticket.ticket_code) && (
              <span className="tkt-code" data-testid="ticket-thread-code">{meta?.ticket_code || ticket.ticket_code}</span>
            )}
            <span className="tkt-subject">{meta?.subject || ticket.subject}</span>
          </span>
        </div>
        {viewer === "admin" && kind !== "support" && (
          <div className="tkt-status-control" data-testid="ticket-status-control">
            <StatusBadge status={status} />
            <select
              className="tkt-status-select"
              value={status}
              onChange={(e) => changeStatus(e.target.value)}
              data-testid="ticket-status-select"
              title="Change ticket status"
            >
              <option value="open">Open</option>
              <option value="in_progress">In progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        )}
        <span className="tkt-thread-id">{ticket.id_label || "—"}</span>
      </div>

      <div className="tkt-log" ref={logRef} data-testid="ticket-messages">
        {msgs.length === 0 && <p className="ib-log-empty">No messages in this ticket yet.</p>}
        {msgs.map((m) => {
          const me = m.sender_role === viewer;
          return (
            <div className={`ib-bubble ${me ? "me" : "them"}`} key={m.id}>
              {!me && <span className="ib-who">{m.sender_name}</span>}
              {m.text && <span className="ib-text">{m.text}</span>}
              {(m.files || []).map((f, i) => (
                <a key={i} className="ib-file" href={`${API}/files/${f.file_id}`} target="_blank" rel="noreferrer">
                  <FileText size={14} /> {f.filename}
                </a>
              ))}
              <span className="ib-time">{fmtTime(m.created_at)}</span>
            </div>
          );
        })}
      </div>

      {files.length > 0 && (
        <div className="ib-attachbar">
          {files.map((f, i) => (
            <span className="ib-chip" key={i}><FileText size={12} /> {f.name}
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}

      <div className="ib-composer">
        <label className="ib-iconbtn" title="Attach files">
          <Paperclip size={18} />
          <input type="file" hidden multiple onChange={addFiles} />
        </label>
        <input
          className="ib-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
          placeholder={`Reply to ${ticket.name}…`}
          data-testid="ticket-reply-input"
        />
        <button className="ib-send" onClick={send} disabled={busy} data-testid="ticket-reply-send"><Send size={16} /></button>
      </div>
    </div>
  );
}

/**
 * Admin ticketing inbox shown under the Communications section.
 * Patient / Doctor subsections, each with Inbox + Sent boxes, search, and a
 * Gmail-like threaded ticket view.
 */
export default function AdminTicketInbox() {
  const { tick } = useMessaging();
  const [kind, setKind] = useState("patient"); // patient | doctor
  const [box, setBox] = useState("inbox");      // inbox | sent
  const [statusFilter, setStatusFilter] = useState("all"); // all|open|in_progress|resolved
  const [q, setQ] = useState("");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tickets", { params: { kind, box, q: q.trim(), status: statusFilter } });
      setTickets(data.tickets || []);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  }, [kind, box, q, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => { load(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const KindIcon = kind === "patient" ? UserRound : kind === "doctor" ? Stethoscope : Headset;
  const placeholder = kind === "patient"
    ? "Search by patient name, email, phone or Patient ID…"
    : kind === "doctor"
      ? "Search by doctor name, email, phone or Doctor ID (NMC)…"
      : "Search by support employee name, email or employee no…";

  return (
    <div className="panel" data-testid="admin-ticket-inbox">
      <div className="tkt-head">
        <div>
          <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}><Ticket size={18} /> Inbox</h3>
          <p className="sub" style={{ margin: "2px 0 0" }}>Received &amp; sent conversations, organised by ticket. Each new query gets a unique <b>#</b> ticket ID.</p>
        </div>
      </div>

      {/* subsection: patient / doctor / customer support */}
      <div className="tkt-subtabs" data-testid="ticket-kind-tabs">
        <button className={`tkt-subtab ${kind === "patient" ? "on" : ""}`} onClick={() => { setKind("patient"); setActive(null); }} data-testid="ticket-kind-patient">
          <UserRound size={15} /> Patient
        </button>
        <button className={`tkt-subtab ${kind === "doctor" ? "on" : ""}`} onClick={() => { setKind("doctor"); setActive(null); }} data-testid="ticket-kind-doctor">
          <Stethoscope size={15} /> Doctor
        </button>
        <button className={`tkt-subtab ${kind === "support" ? "on" : ""}`} onClick={() => { setKind("support"); setActive(null); }} data-testid="ticket-kind-support">
          <Headset size={15} /> Customer Support
        </button>
      </div>

      {active ? (
        <TicketThread ticket={active} kind={kind} onBack={() => { setActive(null); load(); }} onSent={load} onStatusChange={load} />
      ) : (
        <>
          {/* box: inbox / sent */}
          <div className="tkt-boxtabs" data-testid="ticket-box-tabs">
            <button className={`tkt-boxtab ${box === "inbox" ? "on" : ""}`} onClick={() => setBox("inbox")} data-testid="ticket-box-inbox">
              <InboxIcon size={14} /> Inbox
            </button>
            <button className={`tkt-boxtab ${box === "sent" ? "on" : ""}`} onClick={() => setBox("sent")} data-testid="ticket-box-sent">
              <SendHorizonal size={14} /> Sent
            </button>
          </div>

          {/* status filter — not applicable to internal admin↔support threads */}
          {kind !== "support" && (
            <div className="tkt-status-filter" data-testid="ticket-status-filter">
              {[["all", "All"], ["open", "Open"], ["in_progress", "In progress"], ["resolved", "Resolved"]].map(([v, l]) => (
                <button
                  key={v}
                  className={`tkt-status-pill ${v} ${statusFilter === v ? "on" : ""}`}
                  onClick={() => setStatusFilter(v)}
                  data-testid={`ticket-status-filter-${v}`}
                >{l}</button>
              ))}
            </div>
          )}

          <div className="patient-search-bar" style={{ maxWidth: 640, marginBottom: 14 }}>
            <Search size={15} className="patient-search-ic" />
            <input
              className="patient-search-input"
              placeholder={placeholder}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="ticket-search-input"
            />
            {q && <button className="patient-search-clear" onClick={() => setQ("")} data-testid="ticket-search-clear">✕</button>}
          </div>

          <div className="tkt-list" data-testid="ticket-list">
            {loading && <p className="sub" style={{ padding: "18px 4px" }}>Loading…</p>}
            {!loading && tickets.length === 0 && (
              <p className="tkt-empty" data-testid="ticket-empty">
                <MessageSquare size={26} /><br />
                {q ? `No ${kind} tickets match “${q}”.` : `No ${kind} ${box === "inbox" ? "messages received" : "messages sent"} yet.`}
              </p>
            )}
            {!loading && tickets.map((t) => (
              <button key={t.id} className={`tkt-row ${t.unread > 0 ? "unread" : ""}`} onClick={() => setActive(t)} data-testid={`ticket-row-${t.id}`}>
                <span className={`ib-avatar ${kind}`}><KindIcon size={16} /></span>
                <span className="tkt-row-main">
                  <span className="tkt-row-top">
                    {t.ticket_code && <span className="tkt-code" data-testid={`ticket-code-${t.id}`}>{t.ticket_code}</span>}
                    <b className="tkt-row-name">{t.name}</b>
                    <time>{fmtDay(t.last_at)}</time>
                  </span>
                  <span className="tkt-row-subject">{t.subject}</span>
                  <span className="tkt-row-meta">
                    {kind !== "support" && <StatusBadge status={t.status} />}
                    {t.id_label && <span className="pid-chip sm">{t.id_label}</span>}
                    {t.email && <span className="tkt-row-contact">{t.email}</span>}
                    {t.phone && <span className="tkt-row-contact">{t.phone}</span>}
                  </span>
                  <span className="tkt-row-prev">
                    {t.last_sender_role === "admin" ? <span className="tkt-prev-tag">You: </span> : null}
                    {t.last_text || "No messages yet"}
                  </span>
                </span>
                {t.unread > 0 && <span className="ib-unread" data-testid={`ticket-unread-${t.id}`}>{t.unread}</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
